/**
 * ReportAnalysisStatusPanel — Stock Detail → Reports tab.
 *
 * Improvement #1: surfaces the report-analysis state across the most recent
 * annual & interim periods so the user can see at a glance:
 *   ✅ Analysed
 *   ⬇  Downloaded (not yet analysed)
 *   ⚠  Failed
 *   ❌ Not downloaded
 *
 * Plus a header pill summarising overall data confidence.
 */
import React, { useEffect, useState } from "react";
import { apiGet, formatDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, Download, AlertTriangle, XCircle, FileText, Loader2, Clock, MinusCircle } from "lucide-react";

const CONFIDENCE_PILL = {
  high:    { label: "High confidence",    cls: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/40" },
  medium:  { label: "Medium confidence",  cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/40" },
  low:     { label: "Low confidence",     cls: "bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/40" },
  minimal: { label: "Minimal data",       cls: "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40" },
};

const STATUS_META = {
  analysed: {
    Icon: CheckCircle2,
    cls: "text-[hsl(var(--success))]",
    label: "Analysed",
  },
  analysing: {
    Icon: Loader2,
    cls: "text-[hsl(var(--info))] animate-spin",
    label: "Claude is analysing now…",
  },
  queued: {
    Icon: Clock,
    cls: "text-[hsl(var(--info))]",
    label: "Queued for Claude analysis",
  },
  downloaded: {
    Icon: Download,
    cls: "text-blue-500",
    label: "Downloaded, not analysed yet",
  },
  failed: {
    Icon: AlertTriangle,
    cls: "text-[hsl(var(--warning))]",
    label: "Analysis failed",
  },
  skipped: {
    Icon: MinusCircle,
    cls: "text-muted-foreground",
    label: "Skipped by admin — re-enable from Company reports below",
  },
  discovered: {
    Icon: XCircle,
    cls: "text-muted-foreground",
    label: "Not downloaded",
  },
};

const Row = ({ row }) => {
  const meta = STATUS_META[row.status] || STATUS_META.discovered;
  const { Icon } = meta;
  return (
    <li
      className="flex items-center gap-2 py-1.5 text-sm"
      data-testid={`report-status-row-${row.report_period || row.report_id}`}
    >
      <Icon className={`h-3.5 w-3.5 shrink-0 ${meta.cls}`} />
      <span className="font-medium tabular text-xs w-20 shrink-0">
        {row.report_period || (row.report_date ? formatDate(row.report_date) : "—")}
      </span>
      <span className={`text-xs ${meta.cls}`}>— {meta.label}</span>
      {row.title && (
        <span
          className="ml-auto text-[10px] text-muted-foreground truncate max-w-[260px]"
          title={row.title}
        >
          {row.title}
        </span>
      )}
    </li>
  );
};

const Section = ({ title, rows, totalCount }) => (
  <div className="space-y-1">
    <div className="flex items-baseline justify-between">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h4>
      {totalCount != null && (
        <span className="text-[10px] text-muted-foreground tabular">
          {totalCount} on file
        </span>
      )}
    </div>
    {rows.length === 0 ? (
      <div className="rounded-md border border-dashed px-3 py-3 text-center text-xs text-muted-foreground">
        No {title.toLowerCase()} on file yet.
      </div>
    ) : (
      <ul className="divide-y border rounded-md bg-muted/20 px-3" data-testid={`report-status-${title.replace(/\s+/g, "-").toLowerCase()}`}>
        {rows.map((r) => (
          <Row row={r} key={r.report_id || `${r.report_period}-${r.title}`} />
        ))}
      </ul>
    )}
  </div>
);

export const ReportAnalysisStatusPanel = ({ companyCode }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyCode) return;
    let active = true;
    setLoading(true);
    apiGet(`/companies/${companyCode}/report-status`)
      .then((res) => {
        if (active) setData(res);
      })
      .catch(() => {
        if (active) setData(null);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [companyCode]);

  if (loading) {
    return (
      <Card className="border bg-card" data-testid="report-status-panel-loading">
        <CardHeader className="pb-2">
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;
  const pill = CONFIDENCE_PILL[data.data_confidence] || CONFIDENCE_PILL.low;

  return (
    <Card className="border bg-card" data-testid="report-status-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Report Analysis Status
          </CardTitle>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${pill.cls}`}
            data-testid="report-status-confidence-pill"
          >
            {pill.label}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
          <span data-testid="report-status-annual-count">
            Annual analysed: <strong className="text-foreground tabular">{data.annual_reports_analysed}</strong>
          </span>
          <span data-testid="report-status-interim-count">
            Interim analysed: <strong className="text-foreground tabular">{data.interim_reports_analysed}</strong>
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Section title="Annual Reports" rows={data.annual || []} totalCount={data.totals?.annual_total} />
        <Section title="Interim Reports" rows={data.interim || []} totalCount={data.totals?.interim_total} />
        {data.data_confidence === "low" || data.data_confidence === "minimal" ? (
          <div className="rounded-md border border-orange-500/30 bg-orange-500/10 p-3 text-xs text-orange-700 dark:text-orange-300">
            <strong>Tip:</strong> Upload an annual report below or run "Auto-download from CSE" so we can deepen the
            analysis. Confidence rises to <em>medium</em> after the first analysed annual and to <em>high</em> after three.
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default ReportAnalysisStatusPanel;
